<script setup lang="ts">
// The wait that owns a region — a page, a panel, a modal body, a first load — drawn as
// the Lumi glyph with its five rays flickering in sequence. `Spinner` stays for the
// in-place waits (a button, a row, a line of text); this is the one to reach for when
// the whole area is empty until the answer arrives.
//
// No default `label`, for the reason `Spinner` gives: component-lib has no i18n of its
// own, so a hardcoded string would announce one language to every locale (issue 195,
// DR-19a). A caller supplies a translated `label`; without one `role="status"` renders
// with no `aria-label` at all. The glyph is decorative — the wrapper's role and label
// carry the meaning — so the svg is hidden from assistive tech.
//
// `size` is the glyph's height: `panel` for a panel or modal body (about what a
// `Spinner size="md"` panel wait occupies), `page` for a view, and `hero` (236 px) for a
// full-screen moment where the glyph is the subject, as on the onboarding preparing
// screen. The caller centres it (`class="mx-auto"`, a flex parent); a `class` passes
// through to the root.
//
// The drawing is the 512 px brand source, cropped to the glyph. Rays are in source
// order — left bar, left diagonal, vertical, right diagonal, right bar — which is also
// the order they flicker in; the dot and the two letters never move.
//
// The template is deliberately SINGLE-ROOTED with no leading comment: a comment node
// before the root makes the component a fragment, and `wrapper.attributes()` (and
// every host that binds a class or an attr onto it) then finds nothing.
withDefaults(defineProps<{ size?: 'panel' | 'page' | 'hero'; label?: string }>(), {
  size: 'panel',
});
</script>

<template>
  <span class="lumi-loader" :class="`lumi-loader--${size}`" role="status" :aria-label="label">
    <svg viewBox="128 28 288 458" fill="currentColor" aria-hidden="true" focusable="false">
      <path class="lumi-loader__ray lumi-loader__ray--1" d="M133.66,164.38v4.06c0,8.12,6.77,14.89,16.24,14.89h61.46c9.47,0,16.24-6.77,16.24-14.89v-4.06c0-8.12-6.77-14.88-16.24-14.88h-61.46c-9.47,0-16.24,6.77-16.24,14.88Z" />
      <path class="lumi-loader__ray lumi-loader__ray--2" d="M171.11,66.74l-2.87,2.87c-5.74,5.74-5.74,15.31.96,22.01l43.46,43.46c6.7,6.7,16.27,6.7,22.01.96l2.87-2.87c5.74-5.74,5.74-15.31-.96-22.01l-43.46-43.46c-6.7-6.7-16.27-6.7-22.01-.96Z" />
      <path class="lumi-loader__ray lumi-loader__ray--3" d="M268.81,127.12h4.06c8.12,0,14.89-6.77,14.89-16.24v-61.46c0-9.47-6.77-16.24-14.89-16.24h-4.06c-8.12,0-14.88,6.77-14.88,16.24v61.46c0,9.47,6.77,16.24,14.88,16.24Z" />
      <path class="lumi-loader__ray lumi-loader__ray--4" d="M302.73,128.91l2.87,2.87c5.74,5.74,15.31,5.74,22.01-.96l43.46-43.46c6.7-6.7,6.7-16.27.96-22.01l-2.87-2.87c-5.74-5.74-15.31-5.74-22.01.96l-43.46,43.46c-6.7,6.7-6.7,16.27-.96,22.01Z" />
      <path class="lumi-loader__ray lumi-loader__ray--5" d="M316.54,165.7v4.06c0,8.12,6.77,14.89,16.24,14.89h61.46c9.47,0,16.24-6.77,16.24-14.89v-4.06c0-8.12-6.77-14.88-16.24-14.88h-61.46c-9.47,0-16.24,6.77-16.24,14.88Z" />
      <circle cx="270.84" cy="168.54" r="18.05" />
      <path d="M322.88,235.91c0-10.5,7.5-18,16.5-18h4.5c9,0,16.5,7.5,16.5,18v196.52c0,28.5-22.5,51.01-51.01,51.01h-6c-28.5,0-51.01-22.5-51.01-51.01v-196.52c0-10.5,7.5-18,16.5-18h4.5c9,0,16.5,7.5,16.5,18v195.02c0,10.5,6,18,16.5,18s16.5-7.5,16.5-18v-195.02Z" />
      <path d="M218.59,442.57v-206.81c0-10.49-7.5-17.98-16.49-17.98h-4.5c-8.99,0-16.49,7.49-16.49,17.98v226.53c0,10.01,7.51,17.75,14.37,17.75h40.11s-15.68-9.99-17.01-37.47Z" />
    </svg>
  </span>
</template>

<style>
/* Durations and easings come from tokens/motion.css (`check-ds-tokens` refuses an
   undeclared var); the colour is `--accent`, the same ink `Spinner` uses. One cycle
   is `--dur-loading`; each ray dips over the first 40 % of it and starts 12 % of a
   cycle after the one before, so the flicker travels left to right and the glyph is
   whole again for a beat before the first ray goes round once more. The dip is opacity
   plus a small pull towards the dot (the rays are the glyph's light, the dot is where
   it comes from), never a second colour. */
.lumi-loader{display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;line-height:0;color:var(--accent);}
.lumi-loader svg{display:block;height:100%;width:auto;}
.lumi-loader--panel{height:44px;}
.lumi-loader--page{height:64px;}
.lumi-loader--hero{height:236px;}
.lumi-loader__ray{transform-box:view-box;transform-origin:270.84px 168.54px;animation:lumi-loader-flicker var(--dur-loading) var(--ease-standard) infinite;}
.lumi-loader__ray--1{animation-delay:0ms;}
.lumi-loader__ray--2{animation-delay:calc(var(--dur-loading) * 0.12);}
.lumi-loader__ray--3{animation-delay:calc(var(--dur-loading) * 0.24);}
.lumi-loader__ray--4{animation-delay:calc(var(--dur-loading) * 0.36);}
.lumi-loader__ray--5{animation-delay:calc(var(--dur-loading) * 0.48);}

/* THE FROZEN FRAME MUST STILL READ AS THE FULL GLYPH. No ray starts or rests at
   opacity 0: every keyframe that is not the dip is full opacity, and under
   `prefers-reduced-motion` the animation is switched off outright (the global block
   would only shrink it to 0.01ms, which paints the 0% frame — also full — but a
   loader that does not move should not be flickering at an imperceptible rate
   either). The glyph then simply sits there, whole, under its `role="status"`. */
@keyframes lumi-loader-flicker {
  0%, 40%, 100% { opacity: 1; transform: scale(1); }
  20% { opacity: 0.2; transform: scale(0.9); }
}
@media (prefers-reduced-motion: reduce) {
  .lumi-loader__ray{animation:none;opacity:1;transform:none;}
}
</style>
